import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { authService } from './authService';
import { toast } from '@/hooks/use-toast';
import i18n from '@/i18n/config';
import React from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10);
const ENABLE_CONSOLE_LOGS = import.meta.env.VITE_ENABLE_CONSOLE_LOGS === 'true';
const HOOPERS_API_KEY = import.meta.env.VITE_HOOPERS_API_KEY || '';

/**
 * Backend error code for a user whose tenant is deactivated or missing (AL-1684).
 *
 * The API refuses rather than silently serving master data, so the only signal the user would
 * otherwise get is an empty dashboard.
 */
export const TENANT_UNAVAILABLE_CODE = 'tenantUnavailable';

/** True for the 403 the backend returns when the caller's tenant is unusable. */
export const isTenantUnavailable = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) {
    return false;
  }

  const data = error.response.data as { code?: unknown } | undefined;

  return data?.code === TENANT_UNAVAILABLE_CODE;
};

// One toast per burst: a dashboard fires dozens of queries, and every one of them fails with the
// same 403. Without this the user gets a wall of identical toasts.
let lastTenantToastAt = 0;
const TENANT_TOAST_COOLDOWN_MS = 5000;

// Global callback to update user profile context
let userProfileUpdateCallback: ((data: any) => void) | null = null;

// Export function to register callback from UserProfileContext
export const registerUserProfileUpdateCallback = (callback: (data: any) => void) => {
  userProfileUpdateCallback = callback;
};

export const unregisterUserProfileUpdateCallback = () => {
  userProfileUpdateCallback = null;
};

/** The id the request interceptor stamps on a config so the response can find it. */
type RequestWithId = AxiosRequestConfig & { __requestId?: number };

class HttpClient {
  private instance: AxiosInstance;
  private authToken: string | null = null;
  /**
   * In-flight requests, so the session can cancel them all on logout (AL-1782).
   *
   * Keyed by a per-request id rather than by method+url+body. The old key was
   * rebuilt from the config on the way back, where axios has already serialised
   * `data` to a string — so it never matched the one built on the way out, and
   * every POST left an entry behind for the life of the tab.
   */
  private pendingRequests = new Map<number, AbortController>();

  private requestSequence = 0;
  /**
   * The renewal currently in flight, shared by everyone waiting on it (AL-1781).
   *
   * Replaces a queue of `resolve` callbacks. That queue could only report
   * success: when a renewal failed it was emptied without calling anything, so
   * every request parked in it stayed pending for the life of the tab. A shared
   * promise settles both ways, for all of them.
   */
  private refreshInFlight: Promise<string> | null = null;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(HOOPERS_API_KEY && { 'hoopers-api-key': HOOPERS_API_KEY }),
      },
    });

    // Only load token from localStorage if we don't already have one
    // This prevents hot-reload from overwriting a newly set token
    if (!this.authToken) {
      const token = localStorage.getItem(import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token');
      if (token) {
        this.authToken = token;
      }
    }

    this.setupInterceptors();
  }

  private logRequest(config: AxiosRequestConfig) {
    // Logging disabled - use ENABLE_CONSOLE_LOGS env var if needed
  }

  private logResponse(response: AxiosResponse) {
    // Logging disabled - use ENABLE_CONSOLE_LOGS env var if needed
  }

  private logError(error: AxiosError) {
    if (ENABLE_CONSOLE_LOGS) {
      if (error.response) {
        console.error(
          `[API Error] ${error.response.status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
          {
            data: error.response.data,
            status: error.response.status,
            headers: error.response.headers,
          }
        );
      } else if (error.request) {
        console.error('[API Error] No response received', error.request);
      } else {
        console.error('[API Error]', error.message);
      }
    }
  }

  /**
   * Combines the caller's cancellation with our own.
   *
   * The client used to overwrite `config.signal` outright, which is why
   * aborting from React Query or from a service never had any effect.
   */
  private static combineSignals(
    callerSignal: AbortSignal | undefined,
    ownSignal: AbortSignal
  ): AbortSignal {
    if (!callerSignal) return ownSignal;

    if (typeof AbortSignal.any === 'function') {
      return AbortSignal.any([callerSignal, ownSignal]);
    }

    // Older runtimes: mirror both into a third signal by hand.
    const merged = new AbortController();
    const abort = () => merged.abort();

    if (callerSignal.aborted || ownSignal.aborted) {
      merged.abort();
    } else {
      callerSignal.addEventListener('abort', abort, { once: true });
      ownSignal.addEventListener('abort', abort, { once: true });
    }

    return merged.signal;
  }

  /**
   * The endpoints that must never trigger a renewal.
   *
   * `/api/auth/refresh` above all: the renewal call goes out through this same
   * interceptor, so without the exemption it would ask for a renewal, be handed
   * the single in-flight promise — the one that only settles when that very
   * request finishes — and wait on itself for ever.
   *
   * One list rather than two, because the 401 path needs the same answer and
   * the two copies drifting apart is how this sort of thing gets missed.
   */
  private static readonly AUTH_ENDPOINTS = [
    '/api/login',
    '/api/register',
    '/api/auth/refresh',
    '/api/logout',
  ];

  private static isAuthEndpoint(url: string | undefined): boolean {
    return HttpClient.AUTH_ENDPOINTS.some(endpoint => url?.includes(endpoint));
  }

  /** How long before `exp` the client starts renewing. */
  private static readonly REFRESH_MARGIN_MS = 5 * 60 * 1000;

  private storedToken(): string | null {
    return localStorage.getItem(import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token');
  }

  /** Pulls the token out of an Authorization header, whatever shape it arrives in. */
  private static bearerOf(header: unknown): string | null {
    if (typeof header !== 'string' || header === '') return null;

    return header.startsWith('Bearer ') ? header.slice('Bearer '.length) : header;
  }

  /** Milliseconds left on a JWT, or null when it carries no readable `exp`. */
  private static millisUntilExpiry(token: string | null): number | null {
    if (!token) return null;

    const payload = token.split('.')[1];
    if (!payload) return null;

    try {
      const { exp } = JSON.parse(atob(payload));

      return typeof exp === 'number' ? exp * 1000 - Date.now() : null;
    } catch {
      // Not a JWT we can read — the simple `id|token` format, for instance.
      return null;
    }
  }

  /**
   * Renews the session once, however many callers ask at the same time.
   *
   * Everyone shares the same promise, so five requests leaving together cause
   * one renewal rather than five — which matters because the blacklist has no
   * grace period, and each renewal would invalidate the one before it.
   *
   * The handle clears either way, so one refusal does not latch the tab into
   * never trying again.
   */
  private renewSession(): Promise<string> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = authService.refreshToken().finally(() => {
        this.refreshInFlight = null;
      });
    }

    return this.refreshInFlight;
  }

  /**
   * Renews ahead of `exp` when the token is close to it.
   *
   * Waiting for a 401 could never work: `/api/auth/refresh` sits behind
   * `auth:api`, and the JWT guard turns away an expired token before the
   * controller runs. By the time a 401 announced the expiry, renewing was
   * already impossible and the session was over — which is why every session
   * ended at sixty minutes.
   *
   * A failure here is swallowed on purpose. The request still goes out with the
   * token we hold, and if that one really is finished the 401 path takes over.
   * Failing to renew is not itself a reason to refuse to send anything.
   */
  private async ensureFreshToken(url: string | undefined): Promise<void> {
    if (HttpClient.isAuthEndpoint(url)) return;

    const remaining = HttpClient.millisUntilExpiry(this.storedToken());

    if (remaining === null || remaining > HttpClient.REFRESH_MARGIN_MS) return;

    try {
      await this.renewSession();
    } catch {
      // Deliberate: see above.
    }
  }

  /** Drops a request from the in-flight list, whichever way it ended. */
  private forgetRequest(config: AxiosRequestConfig | undefined) {
    const requestId = (config as RequestWithId | undefined)?.__requestId;

    if (requestId !== undefined) {
      this.pendingRequests.delete(requestId);
    }
  }

  private setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      async config => {
        this.logRequest(config);

        // Add hoopers-api-key header
        if (HOOPERS_API_KEY) {
          config.headers['hoopers-api-key'] = HOOPERS_API_KEY;
        }

        // AL-1782: no de-duplication here any more. This used to abort whichever
        // request had gone out first whenever a second one looked identical —
        // but two different query keys can legitimately ask for the same thing
        // at the same time, and the first caller then got an aborted request
        // resolved as `{}`. React Query already de-duplicates by query key,
        // which is the level that knows whether two callers are really one.
        // The list of URL exclusions that had grown around this went with it.
        //
        // AL-1781: registered BEFORE the renewal below, and deliberately so.
        // This is the last synchronous step, so a request parked waiting for a
        // new token is already in the list that ending the session cancels.
        const requestId = ++this.requestSequence;
        const controller = new AbortController();

        (config as RequestWithId).__requestId = requestId;
        config.signal = HttpClient.combineSignals(
          config.signal as AbortSignal | undefined,
          controller.signal
        );
        this.pendingRequests.set(requestId, controller);

        // AL-1781: renew before `exp` rather than after a 401. The refresh route
        // is behind auth:api, so once the token is expired it is too late.
        await this.ensureFreshToken(config.url);

        // Add auth token - ALWAYS check localStorage first to get the most recent
        // token. Read after the renewal, so a request that waited for one leaves
        // with the new token rather than the one that prompted it.
        const token = this.storedToken();
        if (token) {
          this.authToken = token;
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      error => {
        this.logError(error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      response => {
        this.logResponse(response);
        this.forgetRequest(response.config);
        
        // Auto-update UserProfileContext when /api/users/me is called
        // Update on GET (fetch profile) and PATCH (update profile) to ensure metrics are refreshed
        if (response.config.url && response.config.url.includes('/api/users/me') && response.data) {
          // Update on GET (profile fetch) and PATCH (profile update) requests
          const method = response.config.method?.toLowerCase();
          if (method === 'get' || method === 'patch') {
            try {
              if (userProfileUpdateCallback) {
                userProfileUpdateCallback(response.data);
              }
            } catch (error) {
              console.error('[HttpClient] Error updating user profile context:', error);
            }
          }
        }
        
        return response;
      },
      async (error: unknown) => {
        // AL-1782: a cancelled request is a failure, not a success with no body.
        // Resolving it with `{}` put "no data" into the cache and surfaced
        // further up as a TypeError pointing at the wrong thing entirely.
        // React Query discards cancellations on its own.
        if (axios.isCancel(error)) {
          this.forgetRequest((error as { config?: AxiosRequestConfig })?.config);

          return Promise.reject(error);
        }

        // Handle Axios errors
        if (axios.isAxiosError(error)) {
          this.logError(error);

          // Clean up pending requests
          this.forgetRequest(error.config);

          // Tenant deactivated or missing (AL-1684): tell the user, then let the query fail.
          // Not a redirect — the session is valid, the data source is not.
          if (isTenantUnavailable(error)) {
            this.notifyTenantUnavailable();

            return Promise.reject(error);
          }

          // Handle 401 Unauthorized - attempt token refresh before redirecting
          if (error.response?.status === 401 && error.config) {
            const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

            // Skip refresh for auth endpoints (login, refresh itself) or already-retried requests.
            // AL-1779: /api/logout belongs in that list too. A 401 on the way out
            // means the token was already dead, and refreshing it to retry the
            // logout would revive the session the user just asked to end — then
            // bounce them to the login screen through redirectToLogin.
            if (HttpClient.isAuthEndpoint(originalRequest.url) || originalRequest._retry) {
              // Refresh failed or auth endpoint error — redirect to login
              if (this.isTokenExpired()) {
                this.redirectToLogin();
              }
              return Promise.reject(error);
            }

            originalRequest._retry = true;

            // AL-1781: another request may have renewed while this one was in
            // flight, in which case this 401 is about a token that is already
            // history. Renewing again would blacklist the one just issued —
            // blacklist_grace_period is 0 — and end the session the renewal was
            // there to save. Retrying with what we now hold is enough.
            const sentToken = HttpClient.bearerOf(originalRequest.headers?.Authorization);
            const currentToken = this.storedToken();

            if (sentToken && currentToken && sentToken !== currentToken) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${currentToken}`;

              return this.instance(originalRequest);
            }

            try {
              const newToken = await this.renewSession();

              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;

              return this.instance(originalRequest);
            } catch {
              this.redirectToLogin();

              return Promise.reject(error);
            }
          }

          return Promise.reject(error);
        }

        // Handle non-Axios errors
        console.error('Non-Axios error:', error);
        return Promise.reject(error);
      }
    );
  }

  /** Surfaces the tenant-unavailable 403 once per burst, never throwing from the interceptor. */
  private notifyTenantUnavailable(): void {
    const now = Date.now();
    if (now - lastTenantToastAt < TENANT_TOAST_COOLDOWN_MS) {
      return;
    }
    lastTenantToastAt = now;

    try {
      toast({
        variant: 'destructive',
        title: i18n.t('app.common.tenantUnavailableTitle'),
        description: i18n.t('app.common.tenantUnavailableDescription'),
      });
    } catch {
      // A toast failure must never replace the original error the caller is waiting on.
    }
  }

  /**
   * Check if JWT token is expired
   * @returns true if token is expired or invalid, false otherwise
   */
  private isTokenExpired(): boolean {
    const token = localStorage.getItem(import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token');
    if (!token) return true;

    try {
      // Decode JWT payload (base64 encoded middle part)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expirationTime;
    } catch (e) {
      // If we can't decode the token, consider it expired
      return true;
    }
  }

  private redirectToLogin() {
    this.clearAuth();
    localStorage.removeItem(import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token');
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }

  public setAuthToken(token: string | null) {
    this.authToken = token;
    if (token) {
      this.instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.instance.defaults.headers.common['Authorization'];
    }
  }

  public clearAuth() {
    this.authToken = null;
    delete this.instance.defaults.headers.common['Authorization'];

    // Cancel all pending requests on logout
    this.pendingRequests.forEach(controller => controller.abort());
    this.pendingRequests.clear();
  }

  /**
   * Validates that an endpoint starts with /api/ in development and test environments
   * @param url - The endpoint URL to validate
   * @throws Error in test mode if endpoint doesn't start with /api/
   */
  private validateEndpoint(url: string): void {
    // Only validate in development and test environments
    if (import.meta.env.MODE === 'test' || import.meta.env.DEV) {
      if (typeof url === 'string' && !url.startsWith('/api/')) {
        const error = new Error(
          `❌ Invalid API endpoint: "${url}"\n` +
          `All endpoints must start with "/api/"\n` +
          `Expected: "/api/${url.replace(/^\/+/, '')}"`
        );

        // In test mode, throw error to fail the test
        if (import.meta.env.MODE === 'test') {
          throw error;
        }

        // In dev mode, log error but don't block
        console.error(error.message);
      }
    }
  }

  // HTTP Methods
  public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    this.validateEndpoint(url);
    try {
      const response = await this.instance.get<T>(url, { ...config, signal: config?.signal });
      return response.data;
    } catch (error) {
      this.handleError(error, url);
      throw error;
    }
  }

  public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    this.validateEndpoint(url);
    const response = await this.instance.post<T>(url, data, config);
    return response.data;
  }

  public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    this.validateEndpoint(url);
    const response = await this.instance.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    this.validateEndpoint(url);
    const response = await this.instance.delete<T>(url, config);
    return response.data;
  }

  public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    this.validateEndpoint(url);
    const response = await this.instance.patch<T>(url, data, config);
    return response.data;
  }

  private handleError(error: unknown, url: string) {
    // Ignorar erros de requisições canceladas
    if (axios.isCancel(error)) {
      return;
    }

    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        // Request timeout
      } else if (error.response) {
        console.error('Response error:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Request error:', error.message);
      }
    } else if (error instanceof Error) {
      console.error('Error:', error.message);
    } else {
      console.error('Unknown error occurred');
    }
  }
}

// Singleton
export const httpClient = new HttpClient();

// Hook para React components
export const useHttpClient = () => {
  const initialized = React.useRef(false);

  React.useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const authToken = authService.getAccessToken();
    if (authToken) {
      httpClient.setAuthToken(authToken);
    }
  }, []);

  return httpClient;
};

export default httpClient;
